// Mirror-X Admin API client (dual channel)

import { getUserControlApiCandidates, userCloudflareRequest } from './userCloudflareApi';
import { readBootstrapConfig, saveBootstrapConfig } from './bootstrapConfig';
import { getGitHubToken } from './indexedDB';
import {
  readTemplateCatalogCache,
  writeTemplateCatalogCache,
} from './templateCatalogCache';

const PLATFORM_API_BASE =
  import.meta.env.VITE_PLATFORM_API_BASE ||
  import.meta.env.VITE_API_BASE ||
  '/api';
const ADMIN_TOKEN_ENV = import.meta.env.VITE_ADMIN_TOKEN || '';

function normalizePlatformBase(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '/api';
  if (trimmed.startsWith('/')) {
    return trimmed.replace(/\/+$/, '') || '/api';
  }

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withProtocol);
    const pathname =
      parsed.pathname && parsed.pathname !== '/'
        ? parsed.pathname.replace(/\/+$/, '')
        : '/api';
    return `${parsed.origin}${pathname}`;
  } catch {
    return '/api';
  }
}

function normalizeRequestPath(path) {
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
}

function buildApiUrl(base, path) {
  return `${base}${normalizeRequestPath(path)}`;
}

async function getAdminToken() {
  let token = String(ADMIN_TOKEN_ENV || '').trim();

  try {
    const localToken = globalThis.localStorage?.getItem('mirrorx_admin_token');
    if (localToken) {
      token = localToken.trim();
    }
  } catch {
    // Ignore storage errors and fall back to env token.
  }

  return token;
}

async function getStoredGitHubToken() {
  return (await getGitHubToken()) || '';
}

export function getPlatformApiBase() {
  return normalizePlatformBase(PLATFORM_API_BASE);
}

const BOOTSTRAP_PHASE_LABELS = {
  preflight: 'Preflight',
  ensure_resources: 'Ensure resources',
  ensure_worker: 'Ensure worker',
  ensure_route: 'Ensure route',
  wait_dns_propagation: 'Wait DNS propagation',
  verify_domain: 'Verify domain',
  done: 'Done',
};

const BOOTSTRAP_NEXT_ACTION_HINTS = {
  retry_from_current_phase: 'Retry from current phase.',
  rebind_domain: 'Run domain rebind and verify again.',
  update_token: 'Update Cloudflare token and retry.',
  update_token_scope: 'Grant required token scopes and retry.',
  check_control_api: 'Check platform/control API network connectivity.',
  contact_support: 'Contact support and provide traceId.',
  none: 'No additional action required.',
};

function readBootstrapMeta(source = {}) {
  const raw = source && typeof source === 'object' ? source : {};
  return {
    apiVersion: String(raw.apiVersion || '').trim(),
    traceId: String(raw.traceId || '').trim(),
    taskId: String(raw.taskId || '').trim(),
    phase: String(raw.phase || '').trim(),
    state: String(raw.state || '').trim(),
    code: String(raw.code || '').trim(),
    message: String(raw.message || raw.error || '').trim(),
    retryable: Boolean(raw.retryable),
    nextAction: String(raw.nextAction || '').trim(),
    progress: Number.isFinite(Number(raw.progress)) ? Number(raw.progress) : 0,
    requiredScopes: Array.isArray(raw.requiredScopes) ? raw.requiredScopes : [],
    missingScopes: Array.isArray(raw.missingScopes) ? raw.missingScopes : [],
    details: raw.details && typeof raw.details === 'object' ? raw.details : {},
  };
}

export function getBootstrapPhaseLabel(phase = '') {
  const key = String(phase || '').trim();
  return BOOTSTRAP_PHASE_LABELS[key] || key || '';
}

export function getBootstrapNextActionHint(nextAction = '') {
  const key = String(nextAction || '').trim();
  return BOOTSTRAP_NEXT_ACTION_HINTS[key] || '';
}

export function formatBootstrapErrorMessage(errorLike, fallbackMessage = 'Request failed') {
  const meta = readBootstrapMeta(errorLike);
  const base =
    meta.message ||
    String(errorLike?.message || errorLike?.error || '').trim() ||
    String(fallbackMessage || 'Request failed');
  const labels = [];
  if (meta.phase) labels.push(`phase=${getBootstrapPhaseLabel(meta.phase)}`);
  if (meta.code) labels.push(`code=${meta.code}`);
  if (meta.nextAction) labels.push(`next=${meta.nextAction}`);
  return labels.length ? `${base} (${labels.join(', ')})` : base;
}

export function toBootstrapErrorMeta(errorLike) {
  const meta = readBootstrapMeta(errorLike);
  return {
    ...meta,
    phaseLabel: getBootstrapPhaseLabel(meta.phase),
    nextActionHint: getBootstrapNextActionHint(meta.nextAction),
  };
}

function createRequestError(payload, status, fallbackMessage = 'Request failed') {
  const meta = readBootstrapMeta(payload || {});
  const message = formatBootstrapErrorMessage(
    {
      ...meta,
      message: meta.message || String(payload?.error || '').trim() || fallbackMessage,
    },
    fallbackMessage
  );
  const error = new Error(message);
  error.name = 'ApiRequestError';
  error.status = Number(status || 0);
  error.apiVersion = meta.apiVersion;
  error.traceId = meta.traceId;
  error.taskId = meta.taskId;
  error.phase = meta.phase;
  error.state = meta.state;
  error.code = meta.code;
  error.retryable = meta.retryable;
  error.nextAction = meta.nextAction;
  error.progress = meta.progress;
  error.requiredScopes = meta.requiredScopes;
  error.missingScopes = meta.missingScopes;
  error.details = meta.details;
  error.raw = payload || null;
  return error;
}

function pickBootstrapResponseMeta(payload = {}) {
  const meta = readBootstrapMeta(payload);
  return {
    apiVersion: meta.apiVersion,
    traceId: meta.traceId,
    taskId: meta.taskId,
    phase: meta.phase,
    state: meta.state,
    code: meta.code,
    message: meta.message,
    retryable: meta.retryable,
    nextAction: meta.nextAction,
    progress: meta.progress,
    requiredScopes: meta.requiredScopes,
    missingScopes: meta.missingScopes,
    details: meta.details,
  };
}

function resolveBootstrapCloudflareToken(overrideToken = '') {
  const override = String(overrideToken || '').trim();
  if (override) return override;
  const config = readBootstrapConfig() || {};
  return String(config.cloudflareApiToken || '').trim();
}

export async function platformRequest(url, options = {}) {
  const headers = new Headers(options.headers || {});
  const isFormData =
    typeof FormData !== 'undefined' && options.body instanceof FormData;

  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const adminToken = await getAdminToken();
  if (adminToken && !headers.has('X-Admin-Token')) {
    headers.set('X-Admin-Token', adminToken);
  }

  // Security boundary: never forward user Cloudflare token to platform backend.
  if (headers.has('Authorization') || headers.has('X-Cloudflare-Token')) {
    throw new Error('Security policy: user Cloudflare token cannot be sent to platform API.');
  }

  let response;
  try {
    response = await fetch(buildApiUrl(getPlatformApiBase(), url), {
      ...options,
      headers,
    });
  } catch (error) {
    throw createRequestError(
      {
        message: 'Platform API unreachable',
        code: 'CONTROL_API_UNREACHABLE',
        nextAction: 'check_control_api',
      },
      0,
      error?.message || 'Request failed'
    );
  }

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw createRequestError(payload || {}, response.status, `HTTP ${response.status}`);
  }

  return payload;
}

async function platformBootstrapRequest(url, options = {}) {
  const normalizedPath = normalizeRequestPath(url);
  if (!normalizedPath.startsWith('/bootstrap/')) {
    throw new Error('Security policy: platform bootstrap request path is invalid.');
  }

  const {
    cloudflareToken: overrideToken,
    headers: inputHeaders,
    ...restOptions
  } = options;

  const cloudflareToken = resolveBootstrapCloudflareToken(overrideToken);
  if (!cloudflareToken) {
    throw new Error('Cloudflare API token is required. Please complete onboarding first.');
  }

  const headers = new Headers(inputHeaders || {});
  const isFormData =
    typeof FormData !== 'undefined' && restOptions.body instanceof FormData;

  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const adminToken = await getAdminToken();
  if (adminToken && !headers.has('X-Admin-Token')) {
    headers.set('X-Admin-Token', adminToken);
  }

  // For bootstrap routes we intentionally forward user token to platform control plane.
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${cloudflareToken}`);
  }
  if (!headers.has('X-Cloudflare-Token')) {
    headers.set('X-Cloudflare-Token', cloudflareToken);
  }

  let response;
  try {
    response = await fetch(buildApiUrl(getPlatformApiBase(), normalizedPath), {
      ...restOptions,
      headers,
    });
  } catch (error) {
    throw createRequestError(
      {
        message: 'Bootstrap API unreachable',
        code: 'CONTROL_API_UNREACHABLE',
        phase: 'preflight',
        nextAction: 'check_control_api',
      },
      0,
      error?.message || 'Request failed'
    );
  }

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw createRequestError(payload || {}, response.status, `HTTP ${response.status}`);
  }

  return payload;
}

export async function userRequest(url, options = {}) {
  return userCloudflareRequest(url, options);
}

function isTemplateEndpointRetryableError(error) {
  const message = String(error?.message || '').toLowerCase();
  return (
    message.includes('endpoint not found') ||
    message.includes('http 404') ||
    message.includes('not found') ||
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('name not resolved') ||
    message.includes('econnreset')
  );
}

function persistWorkingControlApiBase(baseUrl) {
  const normalized = String(baseUrl || '').trim();
  if (!normalized) return;

  try {
    const current = readBootstrapConfig() || {};
    if (String(current.controlApiBase || '').trim() === normalized) return;
    saveBootstrapConfig({
      ...current,
      controlApiBase: normalized,
      controlApiBaseLocked: true,
    });
  } catch {
    // Ignore local storage failures.
  }
}

async function templateDeployRequest(path, options = {}) {
  const explicitBase = String(options?.baseUrl || '').trim();
  if (explicitBase) {
    return userCloudflareRequest(path, options);
  }

  const candidates = getUserControlApiCandidates();
  if (!candidates.length) {
    return userCloudflareRequest(path, options);
  }

  let lastError = null;
  for (let index = 0; index < candidates.length; index += 1) {
    const baseUrl = candidates[index];
    try {
      const payload = await userCloudflareRequest(path, {
        ...options,
        baseUrl,
      });
      if (index > 0) {
        persistWorkingControlApiBase(baseUrl);
      }
      return payload;
    } catch (error) {
      lastError = error;
      if (!isTemplateEndpointRetryableError(error)) {
        throw error;
      }
    }
  }

  throw lastError || new Error('Template deploy endpoint unavailable');
}

async function templateCatalogRequest(path, options = {}) {
  try {
    return await templateDeployRequest(path, options);
  } catch (controlError) {
    return platformRequest(path, options);
  }
}

function normalizeSiteKey(value) {
  return String(value || '').trim().toLowerCase();
}

const DRAFT_SIGNAL_SYNC_WARNING_KEY = 'mirrorx_draft_signal_sync_warnings_v1';

function readDraftSignalSyncWarnings() {
  try {
    const raw = globalThis.localStorage?.getItem(DRAFT_SIGNAL_SYNC_WARNING_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeDraftSignalSyncWarnings(nextWarnings = {}) {
  try {
    globalThis.localStorage?.setItem(
      DRAFT_SIGNAL_SYNC_WARNING_KEY,
      JSON.stringify(nextWarnings && typeof nextWarnings === 'object' ? nextWarnings : {})
    );
  } catch {
    // Ignore storage failures; they should not block the editing flow.
  }
}

function getDraftSignalSyncWarning(siteKey) {
  const normalizedSiteKey = normalizeSiteKey(siteKey);
  if (!normalizedSiteKey) return null;
  const warning = readDraftSignalSyncWarnings()[normalizedSiteKey];
  if (!warning || typeof warning !== 'object') return null;
  return {
    siteKey: normalizedSiteKey,
    message: String(warning.message || '').trim(),
    source: String(warning.source || '').trim(),
    state: String(warning.state || '').trim(),
    failedAt: String(warning.failedAt || '').trim(),
  };
}

function saveDraftSignalSyncWarning(siteKey, warning = {}) {
  const normalizedSiteKey = normalizeSiteKey(siteKey);
  if (!normalizedSiteKey) return null;
  const warnings = readDraftSignalSyncWarnings();
  warnings[normalizedSiteKey] = {
    siteKey: normalizedSiteKey,
    message: String(warning.message || '').trim(),
    source: String(warning.source || '').trim(),
    state: String(warning.state || '').trim(),
    failedAt: String(warning.failedAt || new Date().toISOString()).trim(),
  };
  writeDraftSignalSyncWarnings(warnings);
  return warnings[normalizedSiteKey];
}

function clearDraftSignalSyncWarning(siteKey) {
  const normalizedSiteKey = normalizeSiteKey(siteKey);
  if (!normalizedSiteKey) return;
  const warnings = readDraftSignalSyncWarnings();
  if (!Object.prototype.hasOwnProperty.call(warnings, normalizedSiteKey)) return;
  delete warnings[normalizedSiteKey];
  writeDraftSignalSyncWarnings(warnings);
}

function normalizeSiteMenuItems(items = []) {
  return Array.isArray(items)
    ? items.map((item) => ({
        itemId: String(item?.itemId || '').trim(),
        parentItemId: item?.parentItemId ? String(item.parentItemId).trim() : null,
        title: String(item?.title || '').trim(),
        url: String(item?.url || '').trim(),
        target: String(item?.target || '_self').trim(),
        sortOrder: Number(item?.sortOrder || 0),
        visible: item?.visible !== false,
        icon: String(item?.icon || '').trim(),
        meta: item?.meta && typeof item.meta === 'object' ? item.meta : {},
        children: normalizeSiteMenuItems(item?.children || []),
      }))
    : [];
}

function normalizeSiteResponse(payload = {}) {
  const site = payload?.site && typeof payload.site === 'object' ? payload.site : payload;
  const readiness = payload?.readiness && typeof payload.readiness === 'object' ? payload.readiness : null;
  const draftSignalSource =
    site?.draftSignal && typeof site.draftSignal === 'object'
      ? site.draftSignal
      : payload?.draftSignal && typeof payload.draftSignal === 'object'
        ? payload.draftSignal
        : null;
  const draftSignal =
    draftSignalSource && typeof draftSignalSource === 'object'
      ? {
          siteKey: normalizeSiteKey(draftSignalSource.siteKey),
          draftVersionId: Number(draftSignalSource.draftVersionId || 0) || null,
          state: String(draftSignalSource.state || '').trim(),
          reason: String(draftSignalSource.reason || '').trim(),
          source: String(draftSignalSource.source || '').trim(),
          updatedAt: String(draftSignalSource.updatedAt || '').trim(),
          lastEditedAt: String(draftSignalSource.lastEditedAt || '').trim(),
          lastPublishedSyncAt: String(draftSignalSource.lastPublishedSyncAt || '').trim(),
        }
      : null;
  const stateMachineSource =
    site?.stateMachine && typeof site.stateMachine === 'object'
      ? site.stateMachine
      : payload?.stateMachine && typeof payload.stateMachine === 'object'
        ? payload.stateMachine
        : null;
  const stateMachine =
    stateMachineSource && typeof stateMachineSource === 'object'
      ? {
          cloudflare:
            stateMachineSource.cloudflare && typeof stateMachineSource.cloudflare === 'object'
              ? {
                  state: String(stateMachineSource.cloudflare.state || '').trim(),
                  label: String(stateMachineSource.cloudflare.label || '').trim(),
                  tone: String(stateMachineSource.cloudflare.tone || '').trim(),
                  reason: String(stateMachineSource.cloudflare.reason || '').trim(),
                }
              : null,
          editor:
            stateMachineSource.editor && typeof stateMachineSource.editor === 'object'
              ? {
                  state: String(stateMachineSource.editor.state || '').trim(),
                  label: String(stateMachineSource.editor.label || '').trim(),
                  tone: String(stateMachineSource.editor.tone || '').trim(),
                  reason: String(stateMachineSource.editor.reason || '').trim(),
                }
              : null,
          publish:
            stateMachineSource.publish && typeof stateMachineSource.publish === 'object'
              ? {
                  state: String(stateMachineSource.publish.state || '').trim(),
                  label: String(stateMachineSource.publish.label || '').trim(),
                  tone: String(stateMachineSource.publish.tone || '').trim(),
                  reason: String(stateMachineSource.publish.reason || '').trim(),
                  canPublish: Boolean(stateMachineSource.publish.canPublish),
                  canRollback: Boolean(stateMachineSource.publish.canRollback),
                }
              : null,
          actions:
            stateMachineSource.actions && typeof stateMachineSource.actions === 'object'
              ? {
                  canManageTemplate: Boolean(stateMachineSource.actions.canManageTemplate),
                  canEditDraft: Boolean(stateMachineSource.actions.canEditDraft),
                  canOpenPreview: Boolean(stateMachineSource.actions.canOpenPreview),
                  canPublish: Boolean(stateMachineSource.actions.canPublish),
                  canRollback: Boolean(stateMachineSource.actions.canRollback),
                }
              : null,
        }
      : null;
  return {
    siteKey: normalizeSiteKey(site?.siteKey),
    domain: String(site?.domain || '').trim(),
    zoneId: String(site?.zoneId || '').trim(),
    workerScriptName: String(site?.workerScriptName || '').trim(),
    templateId: String(site?.templateId || '').trim(),
    templateVersion: String(site?.templateVersion || '').trim(),
    status: String(site?.status || '').trim(),
    bindingReady: Boolean(site?.bindingReady),
    dnsReady: Boolean(site?.dnsReady),
    runtimeReady: Boolean(site?.runtimeReady),
    draftVersionId: Number(site?.draftVersionId || 0) || null,
    publishedVersionId: Number(site?.publishedVersionId || 0) || null,
    createdAt: String(site?.createdAt || '').trim(),
    updatedAt: String(site?.updatedAt || '').trim(),
    readiness,
    stateMachine,
    draftSignal,
    draftSignalSyncWarning: getDraftSignalSyncWarning(site?.siteKey || draftSignal?.siteKey || ''),
  };
}

function normalizeSiteVersion(version = {}) {
  if (!version || typeof version !== 'object') return null;
  return {
    id: Number(version?.id || 0) || null,
    siteKey: normalizeSiteKey(version?.siteKey),
    versionNo: Number(version?.versionNo || 0) || 0,
    versionType: String(version?.versionType || '').trim(),
    baseTemplateId: String(version?.baseTemplateId || '').trim(),
    baseTemplateVersion: String(version?.baseTemplateVersion || '').trim(),
    status: String(version?.status || '').trim(),
    note: String(version?.note || '').trim(),
    createdBy: String(version?.createdBy || '').trim(),
    publishedAt: String(version?.publishedAt || '').trim(),
    rollbackFromVersionId: Number(version?.rollbackFromVersionId || 0) || null,
    createdAt: String(version?.createdAt || '').trim(),
    updatedAt: String(version?.updatedAt || '').trim(),
    isCurrentDraft: Boolean(version?.isCurrentDraft),
    isCurrentPublished: Boolean(version?.isCurrentPublished),
  };
}

function normalizeSitePublishJob(job = {}) {
  if (!job || typeof job !== 'object') return null;
  return {
    id: Number(job?.id || 0) || null,
    siteKey: normalizeSiteKey(job?.siteKey),
    fromVersionId: Number(job?.fromVersionId || 0) || null,
    toVersionId: Number(job?.toVersionId || 0) || null,
    jobType: String(job?.jobType || '').trim(),
    status: String(job?.status || '').trim(),
    snapshotId: String(job?.snapshotId || '').trim(),
    errorMessage: String(job?.errorMessage || '').trim(),
    metadata: job?.metadata && typeof job.metadata === 'object' ? job.metadata : {},
    createdAt: String(job?.createdAt || '').trim(),
    updatedAt: String(job?.updatedAt || '').trim(),
  };
}

async function syncDraftSignal(siteKey, payload = {}) {
  const normalizedSiteKey = normalizeSiteKey(siteKey);
  if (!normalizedSiteKey) return null;

  try {
    const response = await userRequest('/site-draft-signal', {
      method: 'POST',
      body: JSON.stringify({
        siteKey: normalizedSiteKey,
        ...payload,
      }),
    });
    clearDraftSignalSyncWarning(normalizedSiteKey);
    return response;
  } catch (error) {
    console.warn('Failed to sync draft signal:', error);
    return {
      ok: false,
      warning: saveDraftSignalSyncWarning(normalizedSiteKey, {
        message: String(error?.message || 'Failed to sync draft signal.').trim(),
        source: String(payload?.source || '').trim(),
        state: String(payload?.state || '').trim(),
      }),
    };
  }
}

function normalizeSitePageSchema(page = {}) {
  if (!page || typeof page !== 'object') return null;
  return {
    siteVersionId: Number(page?.siteVersionId || 0) || null,
    pageKey: String(page?.pageKey || 'home').trim() || 'home',
    pageType: String(page?.pageType || 'page').trim() || 'page',
    layoutSchema: page?.layoutSchema && typeof page.layoutSchema === 'object' ? page.layoutSchema : {},
    createdAt: String(page?.createdAt || '').trim(),
    updatedAt: String(page?.updatedAt || '').trim(),
  };
}

function normalizeSiteBlockItems(items = []) {
  return Array.isArray(items)
    ? items.map((item) => ({
        blockId: String(item?.blockId || '').trim(),
        parentBlockId: item?.parentBlockId ? String(item.parentBlockId).trim() : null,
        blockType: String(item?.blockType || 'section').trim() || 'section',
        sortOrder: Number(item?.sortOrder || 0) || 0,
        visible: item?.visible !== false,
        props: item?.props && typeof item.props === 'object' ? item.props : {},
        contentBinding:
          item?.contentBinding && typeof item.contentBinding === 'object' ? item.contentBinding : {},
        children: normalizeSiteBlockItems(item?.children || []),
      }))
    : [];
}

function normalizeSiteSeoConfig(payload = {}) {
  if (!payload || typeof payload !== 'object') return null;
  const og = payload?.og && typeof payload.og === 'object' ? payload.og : {};
  return {
    siteVersionId: Number(payload?.siteVersionId || 0) || null,
    pageKey: String(payload?.pageKey || '__site__').trim() || '__site__',
    title: String(payload?.title || '').trim(),
    description: String(payload?.description || '').trim(),
    keywords: Array.isArray(payload?.keywords)
      ? payload.keywords.map((item) => String(item || '').trim()).filter(Boolean)
      : String(payload?.keywords || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
    canonicalMode: String(payload?.canonicalMode || 'auto').trim() || 'auto',
    canonicalUrl: String(payload?.canonicalUrl || '').trim(),
    robots: String(payload?.robots || 'index,follow').trim() || 'index,follow',
    og: {
      type: String(og?.type || 'website').trim() || 'website',
      title: String(og?.title || '').trim(),
      description: String(og?.description || '').trim(),
      imageAssetKey: String(og?.imageAssetKey || '').trim(),
    },
    createdAt: String(payload?.createdAt || '').trim(),
    updatedAt: String(payload?.updatedAt || '').trim(),
  };
}

function normalizeSiteAsset(payload = {}) {
  if (!payload || typeof payload !== 'object') return null;
  return {
    siteKey: normalizeSiteKey(payload?.siteKey),
    assetKey: String(payload?.assetKey || '').trim(),
    assetType: String(payload?.assetType || 'image').trim() || 'image',
    sourceUrl: String(payload?.sourceUrl || '').trim(),
    storageRef: String(payload?.storageRef || '').trim(),
    altText: String(payload?.altText || '').trim(),
    meta: payload?.meta && typeof payload.meta === 'object' ? payload.meta : {},
    createdAt: String(payload?.createdAt || '').trim(),
    updatedAt: String(payload?.updatedAt || '').trim(),
  };
}

// Backward compatible alias. Existing modules importing request() will use user channel.
export async function request(url, options = {}) {
  return userRequest(url, options);
}

// Analytics API (user-owned control worker)
export const analyticsApi = {
  getDashboard: () => userRequest('/analytics/dashboard'),
  getHeatmap: () => userRequest('/analytics/heatmap'),
  getTimeline: (params) => userRequest(`/analytics/timeline?${new URLSearchParams(params || {})}`),
  addToBlacklist: (ip, reason) =>
    userRequest('/analytics/blacklist', {
      method: 'POST',
      body: JSON.stringify({ ip, reason }),
    }),
  removeFromBlacklist: (ip) =>
    userRequest('/analytics/blacklist', {
      method: 'DELETE',
      body: JSON.stringify({ ip }),
    }),
};

// Route API (user-owned control worker)
export const routesApi = {
  list: (params) => userRequest(`/routes?${new URLSearchParams(params || {})}`),
  create: (data) =>
    userRequest('/routes', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    userRequest(`/routes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id) => userRequest(`/routes/${id}`, { method: 'DELETE' }),
  checkSlug: (slug, contentType, excludeId = null) =>
    userRequest('/routes/check-slug', {
      method: 'POST',
      body: JSON.stringify({ slug, contentType, excludeId }),
    }),
};

// Article API (user-owned control worker)
export const articlesApi = {
  list: (params) => userRequest(`/articles?${new URLSearchParams(params || {})}`),
  get: (id) => userRequest(`/articles/${id}`),
  create: (data) =>
    userRequest('/articles', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    userRequest(`/articles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id) => userRequest(`/articles/${id}`, { method: 'DELETE' }),
};

// Product API (user-owned control worker)
export const productsApi = {
  list: (params) => userRequest(`/products?${new URLSearchParams(params || {})}`),
  get: (id) => userRequest(`/products/${id}`),
  create: (data) =>
    userRequest('/products', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    userRequest(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id) => userRequest(`/products/${id}`, { method: 'DELETE' }),
};

// Mirror rules API (user-owned control worker)
export const mirrorApi = {
  listRules: () => userRequest('/mirror/rules'),
  addRule: (data) =>
    userRequest('/mirror/rules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateRule: (id, data) =>
    userRequest(`/mirror/rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteRule: (id) => userRequest(`/mirror/rules/${id}`, { method: 'DELETE' }),
};

// Storage API (user-owned control worker)
export const storageApi = {
  getStatus: () => userRequest('/storage/status'),
  uploadFile: (formData) =>
    userRequest('/storage/upload', {
      method: 'POST',
      body: formData,
      headers: {},
    }),
  verifyGitHubToken: (token) =>
    userRequest('/storage/github/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  createGitHubRepo: (repoName, token) =>
    userRequest('/storage/github/repo', {
      method: 'POST',
      body: JSON.stringify({ repoName, token }),
    }),
  getCommitHistory: async () => {
    const token = await getStoredGitHubToken();
    return userRequest('/storage/github/commits', {
      headers: {
        'X-GitHub-Token': token || '',
      },
    });
  },
  getGithubQuota: async () => {
    const token = await getStoredGitHubToken();
    return userRequest('/storage/github/quota', {
      headers: {
        'X-GitHub-Token': token || '',
      },
    });
  },
};

// Settings API (user-owned control worker)
export const settingsApi = {
  get: () => userRequest('/settings'),
  update: (data) =>
    userRequest('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// KV API (user-owned control worker)
export const kvApi = {
  get: (key) => userRequest(`/kv/get?key=${encodeURIComponent(key)}`),
  set: (key, value, ttl) =>
    userRequest('/kv/set', {
      method: 'POST',
      body: JSON.stringify({ key, value, ttl }),
    }),
  delete: (key) => userRequest(`/kv/delete?key=${encodeURIComponent(key)}`, { method: 'DELETE' }),
};

// User-scene detection API (user-owned control worker)
export const fetchOriginServerStatus = async () => {
  const data = await userRequest('/mirror/source/status');
  const statusCode = Number(data?.statusCode || 0);
  const contentLength = Number(data?.contentLength ?? data?.contentSize ?? 0);

  return {
    ...data,
    statusCode,
    contentLength,
    is404: data?.is404 === true || statusCode === 404,
    hasContent: Boolean(data?.hasContent),
  };
};

export const fetchCloudflareDomainStatuses = async () => {
  const data = await platformBootstrapRequest('/bootstrap/domains/status');
  const zones = Array.isArray(data?.zones)
    ? data.zones.map((item) => ({
        zoneId: String(item?.zoneId || '').trim(),
        domain: String(item?.domain || '').trim(),
        deploymentStatus: String(item?.deploymentStatus || 'unknown'),
        checkedUrl: String(item?.checkedUrl || '').trim(),
        statusCode: Number(item?.statusCode || 0),
        contentLength: Number(item?.contentLength || 0),
        responseTimeMs: Number(item?.responseTimeMs || 0),
        error: String(item?.error || ''),
      }))
    : [];

  return {
    ...pickBootstrapResponseMeta(data),
    ok: Boolean(data?.ok),
    accountId: String(data?.accountId || '').trim(),
    accountName: String(data?.accountName || '').trim(),
    tokenStatus: String(data?.tokenStatus || '').trim(),
    domainCount: Number(data?.domainCount || zones.length),
    zones,
    summary: {
      deployed: Number(data?.summary?.deployed || 0),
      notDeployed: Number(data?.summary?.notDeployed || 0),
      unknown: Number(data?.summary?.unknown || 0),
    },
    recommendedDomain: data?.recommendedDomain
      ? {
          zoneId: String(data.recommendedDomain.zoneId || '').trim(),
          domain: String(data.recommendedDomain.domain || '').trim(),
          deploymentStatus: String(data.recommendedDomain.deploymentStatus || 'not_deployed'),
        }
      : null,
    permissionAudit:
      data?.permissionAudit && typeof data.permissionAudit === 'object'
        ? data.permissionAudit
        : null,
    downgradeRecommendation:
      data?.downgradeRecommendation && typeof data.downgradeRecommendation === 'object'
        ? data.downgradeRecommendation
        : null,
    verifiedAt: String(data?.verifiedAt || ''),
  };
};

export const configureCloudflareDomain = async ({
  token,
  domain,
  zoneId,
  scriptName,
  routePattern,
}) => {
  const resolvedToken = resolveBootstrapCloudflareToken(token);
  if (!resolvedToken) {
    throw new Error('Cloudflare API token is required. Please complete onboarding first.');
  }

  const payload = await platformBootstrapRequest('/bootstrap/domains/configure', {
    method: 'POST',
    cloudflareToken: resolvedToken,
    body: JSON.stringify({
      domain,
      zoneId,
      scriptName,
      routePattern,
    }),
  });

  return {
    ...pickBootstrapResponseMeta(payload),
    ok: Boolean(payload?.ok),
    accountId: String(payload?.accountId || '').trim(),
    accountName: String(payload?.accountName || '').trim(),
    zoneId: String(payload?.zoneId || '').trim(),
    zoneName: String(payload?.zoneName || '').trim(),
    domain: String(payload?.domain || '').trim(),
    routePattern: String(payload?.routePattern || '').trim(),
    dns: {
      apex: String(payload?.dns?.apex || '').trim(),
      hostname: String(payload?.dns?.hostname || '').trim(),
    },
    customDomainBinding:
      payload?.customDomainBinding && typeof payload.customDomainBinding === 'object'
        ? payload.customDomainBinding
        : null,
    actions: Array.isArray(payload?.actions) ? payload.actions : [],
    deploymentStatus: payload?.deploymentStatus || null,
    verifiedAt: String(payload?.verifiedAt || ''),
  };
};

export const fetchCloudflareDomainConfigurationStatus = async ({ domain, zoneId, scriptName }) => {
  const search = new URLSearchParams();
  if (domain) search.set('domain', domain);
  if (zoneId) search.set('zoneId', zoneId);
  if (scriptName) search.set('scriptName', scriptName);

  const data = await platformBootstrapRequest(
    `/bootstrap/domains/configuration-status?${search.toString()}`
  );

  return {
    ...pickBootstrapResponseMeta(data),
    ok: Boolean(data?.ok),
    accountId: String(data?.accountId || '').trim(),
    accountName: String(data?.accountName || '').trim(),
    zoneId: String(data?.zoneId || '').trim(),
    zoneName: String(data?.zoneName || '').trim(),
    domain: String(data?.domain || '').trim(),
    dns: {
      apex: String(data?.dns?.apex || '').trim(),
      hostname: String(data?.dns?.hostname || '').trim(),
    },
    customDomainBinding:
      data?.customDomainBinding && typeof data.customDomainBinding === 'object'
        ? data.customDomainBinding
        : null,
    workerRouteBinding:
      data?.workerRouteBinding && typeof data.workerRouteBinding === 'object'
        ? data.workerRouteBinding
        : null,
    dnsRecords: Array.isArray(data?.dnsRecords) ? data.dnsRecords : [],
    workerRoutes: Array.isArray(data?.workerRoutes) ? data.workerRoutes : [],
    workerDomains: Array.isArray(data?.workerDomains) ? data.workerDomains : [],
    bindingMatchesScript:
      data?.bindingMatchesScript === true || data?.bindingMatchesScript === false
        ? data.bindingMatchesScript
        : null,
    customDomainMatchesScript:
      data?.customDomainMatchesScript === true || data?.customDomainMatchesScript === false
        ? data.customDomainMatchesScript
        : null,
    workerRouteMatchesScript:
      data?.workerRouteMatchesScript === true || data?.workerRouteMatchesScript === false
        ? data.workerRouteMatchesScript
        : null,
    deploymentStatus: data?.deploymentStatus || null,
    verifiedAt: String(data?.verifiedAt || ''),
  };
};

export const fetchD1RouteCount = async () => {
  try {
    const data = await userRequest('/routes/count');
    return data.count || 0;
  } catch (error) {
    console.warn('读取 D1 路由数量失败，默认返回 0。', error);
    return 0;
  }
};

// Translate API (user-owned control worker)
export const translateApi = {
  translate: (text, source = 'auto', target = 'en') =>
    userRequest('/translate', {
      method: 'POST',
      body: JSON.stringify({ text, source, target }),
    }),
  generateSlug: (text, contentType = 'article', template = null) =>
    userRequest('/translate/slug', {
      method: 'POST',
      body: JSON.stringify({ text, contentType, template }),
    }),
};

// Template market API (platform backend only)
export const templateMarketApi = {
  listWithCache: async () => {
    try {
      const payload = await templateCatalogRequest('/templates');
      const templates = payload.templates || payload.items || [];
      if (Array.isArray(templates) && templates.length > 0) {
        const cached = writeTemplateCatalogCache(templates, {
          platformUpdatedAt: payload.updatedAt || '',
        });
        return {
          templates,
          source: 'platform',
          cachedAt: cached?.cachedAt || '',
          platformUpdatedAt: payload.updatedAt || '',
        };
      }
    } catch (error) {
      const cached = readTemplateCatalogCache();
      if (cached?.templates?.length) {
        return {
          templates: cached.templates,
          source: 'cache',
          cachedAt: cached.cachedAt || '',
          platformUpdatedAt: cached.platformUpdatedAt || '',
          error: error?.message || 'Template market unavailable',
        };
      }
      return {
        templates: [],
        source: 'empty',
        error: error?.message || 'Template market unavailable',
      };
    }

    const cached = readTemplateCatalogCache();
    if (cached?.templates?.length) {
      return {
        templates: cached.templates,
        source: 'cache',
        cachedAt: cached.cachedAt || '',
        platformUpdatedAt: cached.platformUpdatedAt || '',
      };
    }

    return { templates: [], source: 'empty' };
  },
  list: async () => {
    const result = await templateMarketApi.listWithCache();
    return result.templates || [];
  },
  getByIdWithCache: async (templateId) => {
    const normalizedTemplateId = String(templateId || '').trim();
    if (!normalizedTemplateId) return null;
    try {
      const payload = await templateCatalogRequest(`/templates/${encodeURIComponent(normalizedTemplateId)}`);
      const template =
        payload?.template && typeof payload.template === 'object'
          ? payload.template
          : payload && typeof payload === 'object'
            ? payload
            : null;
      if (template?.id) {
        return template;
      }
    } catch {
      // Fall back to cached list lookup below.
    }
    const templates = await templateMarketApi.list();
    return templates.find((item) => String(item?.id || '').trim() === normalizedTemplateId) || null;
  },
  createDeployTicket: ({
    templateId,
    version = '',
    siteKey = 'default',
    targetDomain = '',
    zoneId = '',
  } = {}) =>
    templateCatalogRequest(
      `/templates/deploy-ticket?templateId=${encodeURIComponent(String(templateId || ''))}&version=${encodeURIComponent(
        String(version || '')
      )}&siteKey=${encodeURIComponent(String(siteKey || 'default'))}&targetDomain=${encodeURIComponent(
        String(targetDomain || '')
      )}&zoneId=${encodeURIComponent(String(zoneId || ''))}`
    ),
};

// Template deployment API (user-owned control worker)
export const templateDeployApi = {
  getCurrent: (siteKey = '') =>
    templateDeployRequest(`/templates/current?siteKey=${encodeURIComponent(siteKey || '')}`),
  listDeployments: ({ siteKey = '', limit = 20, offset = 0 } = {}) =>
    templateDeployRequest(
      `/templates/deployments?siteKey=${encodeURIComponent(siteKey)}&limit=${encodeURIComponent(
        limit
      )}&offset=${encodeURIComponent(offset)}`
    ),
  deploy: (payload) =>
    templateDeployRequest('/templates/deploy', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    }),
  update: (payload) =>
    templateDeployRequest('/templates/update', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    }),
  rollback: (payload) =>
    templateDeployRequest('/templates/rollback', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    }),
};

export const siteBuilderApi = {
  listSites: async () => {
    const payload = await userRequest('/sites');
    return Array.isArray(payload?.sites) ? payload.sites.map((item) => normalizeSiteResponse(item)) : [];
  },
  getState: async (siteKey) => {
    const payload = await userRequest(`/site-state?siteKey=${encodeURIComponent(normalizeSiteKey(siteKey))}`);
    return {
      site: normalizeSiteResponse(payload?.site),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      publishedVersion: normalizeSiteVersion(payload?.publishedVersion || null),
      jobs: Array.isArray(payload?.jobs) ? payload.jobs.map((item) => normalizeSitePublishJob(item)).filter(Boolean) : [],
      stateMachine: payload?.stateMachine && typeof payload.stateMachine === 'object' ? payload.stateMachine : null,
      draftSignal:
        payload?.draftSignal && typeof payload.draftSignal === 'object'
          ? normalizeSiteResponse({ draftSignal: payload.draftSignal }).draftSignal
          : null,
    };
  },
  createSite: async (data) => {
    const payload = await userRequest('/sites', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
    if (payload?.draftVersion?.id) {
      await syncDraftSignal(normalizeSiteKey(data?.siteKey || data?.domain || ''), {
        state: 'draft_saved',
        draftVersionId: payload.draftVersion.id,
        reason: 'Draft workspace was initialized for this site.',
        source: 'site_create',
      });
    }
    return {
      site: normalizeSiteResponse(payload?.site),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
    };
  },
  getSite: async (siteKey) => {
    const payload = await userRequest(`/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}`);
    return {
      site: normalizeSiteResponse(payload?.site),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      publishedVersion: normalizeSiteVersion(payload?.publishedVersion || null),
    };
  },
  getDraft: async (siteKey) => {
    const payload = await userRequest(`/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft`);
    return {
      site: normalizeSiteResponse(payload?.site),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      navigation: {
        menuKey: String(payload?.navigation?.menuKey || 'primary').trim(),
        items: normalizeSiteMenuItems(payload?.navigation?.items || []),
      },
    };
  },
  listDraftPages: async (siteKey) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/pages`
    );
    return {
      site: normalizeSiteResponse(payload?.site),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      pages: Array.isArray(payload?.pages)
        ? payload.pages.map((page) => normalizeSitePageSchema(page)).filter(Boolean)
        : [],
    };
  },
  createDraftPage: async (siteKey, data = {}) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/pages`,
      {
        method: 'POST',
        body: JSON.stringify(data || {}),
      }
    );
    return {
      site: normalizeSiteResponse(payload?.site),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      page: normalizeSitePageSchema(payload?.page || null),
      pages: Array.isArray(payload?.pages)
        ? payload.pages.map((page) => normalizeSitePageSchema(page)).filter(Boolean)
        : [],
      blocks: normalizeSiteBlockItems(payload?.blocks || []),
    };
  },
  getDraftPage: async (siteKey, pageKey = 'home') => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/pages/${encodeURIComponent(
        String(pageKey || 'home').trim() || 'home'
      )}`
    );
    return {
      site: normalizeSiteResponse(payload?.site),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      page: normalizeSitePageSchema(payload?.page || null),
      blocks: normalizeSiteBlockItems(payload?.blocks || []),
    };
  },
  updateDraftPageSchema: async (siteKey, pageKey = 'home', data = {}) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/pages/${encodeURIComponent(
        String(pageKey || 'home').trim() || 'home'
      )}/schema`,
      {
        method: 'PUT',
        body: JSON.stringify(data || {}),
      }
    );
    await syncDraftSignal(siteKey, {
      state: 'draft_dirty',
      reason: `Draft schema for ${String(pageKey || 'home').trim() || 'home'} was updated.`,
      source: 'draft_page_schema_update',
    });
    return {
      page: normalizeSitePageSchema(payload?.page || null),
      blocks: normalizeSiteBlockItems(payload?.blocks || []),
    };
  },
  createDraftPageBlock: async (siteKey, pageKey = 'home', data = {}) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/pages/${encodeURIComponent(
        String(pageKey || 'home').trim() || 'home'
      )}/blocks`,
      {
        method: 'POST',
        body: JSON.stringify(data || {}),
      }
    );
    await syncDraftSignal(siteKey, {
      state: 'draft_dirty',
      reason: `A ${String(data?.blockType || 'section').trim() || 'section'} block was added.`,
      source: 'draft_block_create',
    });
    return {
      block:
        payload?.block && typeof payload.block === 'object'
          ? normalizeSiteBlockItems([payload.block])[0] || null
          : null,
      page: normalizeSitePageSchema(payload?.page || null),
      blocks: normalizeSiteBlockItems(payload?.blocks || []),
    };
  },
  updateDraftPageBlock: async (siteKey, pageKey = 'home', blockId, data = {}) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/pages/${encodeURIComponent(
        String(pageKey || 'home').trim() || 'home'
      )}/blocks/${encodeURIComponent(String(blockId || '').trim())}`,
      {
        method: 'PUT',
        body: JSON.stringify(data || {}),
      }
    );
    await syncDraftSignal(siteKey, {
      state: 'draft_dirty',
      reason: `Draft block ${String(blockId || '').trim()} was updated.`,
      source: 'draft_block_update',
    });
    return {
      page: normalizeSitePageSchema(payload?.page || null),
      blocks: normalizeSiteBlockItems(payload?.blocks || []),
    };
  },
  deleteDraftPageBlock: async (siteKey, pageKey = 'home', blockId) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/pages/${encodeURIComponent(
        String(pageKey || 'home').trim() || 'home'
      )}/blocks/${encodeURIComponent(String(blockId || '').trim())}`,
      {
        method: 'DELETE',
      }
    );
    await syncDraftSignal(siteKey, {
      state: 'draft_dirty',
      reason: `Draft block ${String(blockId || '').trim()} was removed.`,
      source: 'draft_block_delete',
    });
    return {
      page: normalizeSitePageSchema(payload?.page || null),
      blocks: normalizeSiteBlockItems(payload?.blocks || []),
    };
  },
  reorderDraftPageBlocks: async (siteKey, pageKey = 'home', items = []) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/pages/${encodeURIComponent(
        String(pageKey || 'home').trim() || 'home'
      )}/reorder`,
      {
        method: 'POST',
        body: JSON.stringify({ items }),
      }
    );
    await syncDraftSignal(siteKey, {
      state: 'draft_dirty',
      reason: `Draft block order changed on ${String(pageKey || 'home').trim() || 'home'}.`,
      source: 'draft_block_reorder',
    });
    return {
      page: normalizeSitePageSchema(payload?.page || null),
      blocks: normalizeSiteBlockItems(payload?.blocks || []),
    };
  },
  getDraftSeoConfig: async (siteKey, pageKey = '') => {
    const normalizedPageKey = String(pageKey || '').trim();
    const suffix =
      normalizedPageKey && normalizedPageKey !== '__site__'
        ? `?pageKey=${encodeURIComponent(normalizedPageKey)}`
        : '';
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/seo${suffix}`
    );
    return {
      siteKey: normalizeSiteKey(payload?.siteKey || siteKey),
      draftVersionId: Number(payload?.draftVersionId || 0) || null,
      pageKey: String(payload?.pageKey || '__site__').trim() || '__site__',
      seo: normalizeSiteSeoConfig(payload?.seo || null),
    };
  },
  updateDraftSeoConfig: async (siteKey, data = {}) => {
    const normalizedPageKey = String(data?.pageKey || '').trim();
    const suffix =
      normalizedPageKey && normalizedPageKey !== '__site__'
        ? `?pageKey=${encodeURIComponent(normalizedPageKey)}`
        : '';
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/seo${suffix}`,
      {
        method: 'PUT',
        body: JSON.stringify(data || {}),
      }
    );
    await syncDraftSignal(siteKey, {
      state: 'draft_dirty',
      reason: `Draft SEO for ${String(normalizedPageKey || '__site__').trim() || '__site__'} was updated.`,
      source: 'draft_seo_update',
    });
    return {
      siteKey: normalizeSiteKey(payload?.siteKey || siteKey),
      draftVersionId: Number(payload?.draftVersionId || 0) || null,
      pageKey: String(payload?.pageKey || normalizedPageKey || '__site__').trim() || '__site__',
      seo: normalizeSiteSeoConfig(payload?.seo || null),
    };
  },
  listAssets: async (siteKey) => {
    const payload = await userRequest(`/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/assets`);
    return {
      siteKey: normalizeSiteKey(payload?.siteKey || siteKey),
      assets: Array.isArray(payload?.assets)
        ? payload.assets.map((item) => normalizeSiteAsset(item)).filter(Boolean)
        : [],
    };
  },
  createAsset: async (siteKey, data = {}) => {
    const payload = await userRequest(`/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/assets`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
    return {
      siteKey: normalizeSiteKey(payload?.siteKey || siteKey),
      asset: normalizeSiteAsset(payload?.asset || null),
      assets: Array.isArray(payload?.assets)
        ? payload.assets.map((item) => normalizeSiteAsset(item)).filter(Boolean)
        : [],
    };
  },
  updateAsset: async (siteKey, assetKey, data = {}) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/assets/${encodeURIComponent(
        String(assetKey || '').trim()
      )}`,
      {
        method: 'PUT',
        body: JSON.stringify(data || {}),
      }
    );
    return {
      siteKey: normalizeSiteKey(payload?.siteKey || siteKey),
      asset: normalizeSiteAsset(payload?.asset || null),
      assets: Array.isArray(payload?.assets)
        ? payload.assets.map((item) => normalizeSiteAsset(item)).filter(Boolean)
        : [],
    };
  },
  deleteAsset: async (siteKey, assetKey) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/assets/${encodeURIComponent(
        String(assetKey || '').trim()
      )}`,
      {
        method: 'DELETE',
      }
    );
    return {
      siteKey: normalizeSiteKey(payload?.siteKey || siteKey),
      assetKey: String(payload?.assetKey || assetKey || '').trim(),
      assets: Array.isArray(payload?.assets)
        ? payload.assets.map((item) => normalizeSiteAsset(item)).filter(Boolean)
        : [],
    };
  },
  getDraftPreviewUrl: (siteKey, pageKey = '') => {
    const normalizedSiteKey = normalizeSiteKey(siteKey);
    if (!normalizedSiteKey) {
      return '';
    }

    const candidates = getUserControlApiCandidates();
    const preferredBase = candidates.find((item) => String(item || '').trim()) || getPlatformApiBase();
    const baseUrl = buildApiUrl(
      normalizePlatformBase(preferredBase),
      `/sites/${encodeURIComponent(normalizedSiteKey)}/draft/preview`
    );
    const normalizedPageKey = String(pageKey || '').trim();
    if (!normalizedPageKey) {
      return baseUrl;
    }
    return `${baseUrl}?pageKey=${encodeURIComponent(normalizedPageKey)}`;
  },
  applyTemplate: async (siteKey, data) => {
    const payload = await userRequest(`/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/template`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
    await syncDraftSignal(siteKey, {
      state: 'draft_saved',
      draftVersionId: payload?.draftVersion?.id || null,
      reason: `Draft was reset to template ${String(data?.templateId || '').trim() || 'selected template'}.`,
      source: 'template_apply',
    });
    return {
      site: normalizeSiteResponse(payload?.site),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      template: payload?.template || null,
    };
  },
  getDraftMenu: async (siteKey, menuKey = 'primary') => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/menus/${encodeURIComponent(menuKey)}`
    );
    return {
      siteKey: normalizeSiteKey(payload?.siteKey || siteKey),
      draftVersionId: Number(payload?.draftVersionId || 0) || null,
      menuKey: String(payload?.menuKey || menuKey).trim(),
      items: normalizeSiteMenuItems(payload?.items || []),
    };
  },
  createDraftMenuItem: async (siteKey, menuKey = 'primary', data) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/menus/${encodeURIComponent(menuKey)}/items`,
      {
        method: 'POST',
        body: JSON.stringify(data || {}),
      }
    );
    await syncDraftSignal(siteKey, {
      state: 'draft_dirty',
      reason: `Draft navigation added menu item ${String(data?.title || 'Untitled').trim() || 'Untitled'}.`,
      source: 'draft_menu_create',
    });
    return {
      item: payload?.item || null,
      items: normalizeSiteMenuItems(payload?.items || []),
    };
  },
  updateDraftMenuItem: async (siteKey, menuKey = 'primary', itemId, data) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/menus/${encodeURIComponent(menuKey)}/items/${encodeURIComponent(
        String(itemId || '').trim()
      )}`,
      {
        method: 'PUT',
        body: JSON.stringify(data || {}),
      }
    );
    await syncDraftSignal(siteKey, {
      state: 'draft_dirty',
      reason: `Draft navigation updated menu item ${String(itemId || '').trim()}.`,
      source: 'draft_menu_update',
    });
    return {
      items: normalizeSiteMenuItems(payload?.items || []),
    };
  },
  deleteDraftMenuItem: async (siteKey, menuKey = 'primary', itemId) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/menus/${encodeURIComponent(menuKey)}/items/${encodeURIComponent(
        String(itemId || '').trim()
      )}`,
      {
        method: 'DELETE',
      }
    );
    await syncDraftSignal(siteKey, {
      state: 'draft_dirty',
      reason: `Draft navigation removed menu item ${String(itemId || '').trim()}.`,
      source: 'draft_menu_delete',
    });
    return {
      items: normalizeSiteMenuItems(payload?.items || []),
    };
  },
  reorderDraftMenu: async (siteKey, menuKey = 'primary', items) => {
    const payload = await userRequest(
      `/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/draft/menus/${encodeURIComponent(menuKey)}/reorder`,
      {
        method: 'POST',
        body: JSON.stringify({ items }),
      }
    );
    await syncDraftSignal(siteKey, {
      state: 'draft_dirty',
      reason: 'Draft navigation order changed.',
      source: 'draft_menu_reorder',
    });
    return {
      items: normalizeSiteMenuItems(payload?.items || []),
    };
  },
  listVersions: async (siteKey, params = {}) => {
    const query = new URLSearchParams(params || {});
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const payload = await userRequest(`/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/versions${suffix}`);
    return {
      site: normalizeSiteResponse(payload?.site),
      versions: Array.isArray(payload?.versions) ? payload.versions.map((item) => normalizeSiteVersion(item)) : [],
      pagination: payload?.pagination || null,
    };
  },
  listPublishJobs: async (siteKey, params = {}) => {
    const query = new URLSearchParams(params || {});
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const payload = await userRequest(`/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/publish-jobs${suffix}`);
    return {
      site: normalizeSiteResponse(payload?.site),
      jobs: Array.isArray(payload?.jobs) ? payload.jobs.map((item) => normalizeSitePublishJob(item)) : [],
      pagination: payload?.pagination || null,
    };
  },
  publishDraft: async (siteKey, data = {}) => {
    const payload = await userRequest(`/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/publish`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
    await syncDraftSignal(siteKey, {
      state: 'draft_saved',
      draftVersionId: payload?.draftVersion?.id || null,
      reason: 'Draft was cloned from the latest published version.',
      source: 'publish_clone',
    });
    return {
      site: normalizeSiteResponse(payload?.site),
      publishedVersion: normalizeSiteVersion(payload?.publishedVersion || null),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      previousPublishedVersion: payload?.previousPublishedVersion
        ? normalizeSiteVersion(payload.previousPublishedVersion)
        : null,
      job: normalizeSitePublishJob(payload?.job || null),
    };
  },
  rollbackToVersion: async (siteKey, data = {}) => {
    const payload = await userRequest(`/sites/${encodeURIComponent(normalizeSiteKey(siteKey))}/rollback`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
    await syncDraftSignal(siteKey, {
      state: 'draft_saved',
      draftVersionId: payload?.draftVersion?.id || null,
      reason: 'Draft was cloned from the selected rollback version.',
      source: 'rollback_clone',
    });
    return {
      site: normalizeSiteResponse(payload?.site),
      publishedVersion: normalizeSiteVersion(payload?.publishedVersion || null),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      rollbackTargetVersion: payload?.rollbackTargetVersion
        ? normalizeSiteVersion(payload.rollbackTargetVersion)
        : null,
      job: normalizeSitePublishJob(payload?.job || null),
    };
  },
  getMigrationStatus: async (siteKey) => {
    const payload = await userRequest(`/site-migration/status?siteKey=${encodeURIComponent(normalizeSiteKey(siteKey))}`);
    return {
      site: normalizeSiteResponse(payload?.site),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      publishedVersion: normalizeSiteVersion(payload?.publishedVersion || null),
      migration: payload?.migration && typeof payload.migration === 'object' ? payload.migration : null,
    };
  },
  runLegacyMigration: async (siteKey, data = {}) => {
    const payload = await userRequest('/site-migration/run', {
      method: 'POST',
      body: JSON.stringify({
        siteKey: normalizeSiteKey(siteKey),
        ...(data || {}),
      }),
    });
    return {
      site: normalizeSiteResponse(payload?.site),
      draftVersion: normalizeSiteVersion(payload?.draftVersion || null),
      publishedVersion: normalizeSiteVersion(payload?.publishedVersion || null),
      migration: payload?.migration && typeof payload.migration === 'object' ? payload.migration : null,
    };
  },
};

export const bootstrapApi = {
  provision: (token, options = {}) =>
    (() => {
      const cfg = readBootstrapConfig() || {};
      const explicitControlDomain = String(options.controlDomain || '').trim();
      const explicitControlZoneId = String(options.controlZoneId || '').trim();
      const explicitControlScriptName = String(options.controlScriptName || '').trim();

      const persistedDomain = String(cfg?.domainCheckResult?.selectedDomain || '').trim();
      const persistedZoneId = String(
        cfg?.domainCheckResult?.selectedZoneId || cfg?.zoneId || ''
      ).trim();
      const persistedScriptName = String(cfg?.workerScriptName || '').trim();
      // Safety: auto-attach only dedicated API host from persisted domain scan context.
      const persistedDomainIsControlHost = persistedDomain.toLowerCase().startsWith('api.');

      const controlDomain = explicitControlDomain || (persistedDomainIsControlHost ? persistedDomain : '');
      const controlZoneId = explicitControlZoneId || (persistedDomainIsControlHost ? persistedZoneId : '');
      const controlScriptName = explicitControlScriptName || persistedScriptName;

      return platformBootstrapRequest('/bootstrap/provision', {
        method: 'POST',
        cloudflareToken: String(token || '').trim(),
        body: JSON.stringify({
          idempotencyKey: String(options.idempotencyKey || '').trim() || undefined,
          controlApiCandidates: getUserControlApiCandidates({
            preferredBase: options.preferredControlApiBase || '',
          }),
          autoCreateResources: options.autoCreateResources !== false,
          resourceNames:
            options.resourceNames && typeof options.resourceNames === 'object'
              ? options.resourceNames
              : undefined,
          controlDomain: controlDomain || undefined,
          controlZoneId: controlZoneId || undefined,
          controlScriptName: controlScriptName || undefined,
        }),
      });
    })(),
  getTask: (taskId, token = '') =>
    platformBootstrapRequest(`/bootstrap/tasks/${encodeURIComponent(String(taskId || '').trim())}`, {
      method: 'GET',
      cloudflareToken: String(token || '').trim(),
    }),
  retryTask: (taskId, { token = '', fromPhase = '', forceImmediate = false } = {}) =>
    platformBootstrapRequest(`/bootstrap/tasks/${encodeURIComponent(String(taskId || '').trim())}/retry`, {
      method: 'POST',
      cloudflareToken: String(token || '').trim(),
      body: JSON.stringify({
        fromPhase: String(fromPhase || '').trim() || undefined,
        forceImmediate: Boolean(forceImmediate),
      }),
    }),
  rebindDomain: ({
    token = '',
    domain = '',
    zoneId = '',
    scriptName = '',
    idempotencyKey = '',
  } = {}) =>
    platformBootstrapRequest('/bootstrap/domains/rebind', {
      method: 'POST',
      cloudflareToken: String(token || '').trim(),
      body: JSON.stringify({
        idempotencyKey:
          String(idempotencyKey || '').trim() ||
          `ui_rebind_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        domain: String(domain || '').trim(),
        zoneId: String(zoneId || '').trim(),
        scriptName: String(scriptName || '').trim(),
      }),
    }),
};

export const saasPublishApi = {
  publishArticle: ({ token, accountId, databaseId, data }) =>
    platformRequest('/saas/publish/article', {
      method: 'POST',
      body: JSON.stringify({
        token: String(token || '').trim(),
        accountId: String(accountId || '').trim(),
        databaseId: String(databaseId || '').trim(),
        data: data && typeof data === 'object' ? data : {},
      }),
    }),
  publishProduct: ({ token, accountId, databaseId, data }) =>
    platformRequest('/saas/publish/product', {
      method: 'POST',
      body: JSON.stringify({
        token: String(token || '').trim(),
        accountId: String(accountId || '').trim(),
        databaseId: String(databaseId || '').trim(),
        data: data && typeof data === 'object' ? data : {},
      }),
    }),
};

export const URL_TEMPLATES = {
  article: '/blog/{slug}',
  blog: '/blog/{slug}',
  product: '/p/{slug}',
  page: '/{slug}',
  category: '/category/{slug}',
  tag: '/tag/{slug}',
};

export function getUrlTemplate(contentType) {
  return URL_TEMPLATES[contentType] || URL_TEMPLATES.page;
}

